import { withNamespaces } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Badge,
    Button,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    UncontrolledTooltip
} from "reactstrap";
import { useMutation, useQueries, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { connect } from "react-redux";
import axios from "axios";
import { useParams, useHistory, useLocation } from "react-router-dom";
import {
    API_BASE_URL,
    API_URL_RISK_AI_GET_ADDITIONAL_DETAILS,
    CHAT_BOT_DOMAIN,
    PROVIDER_CONFORMITY_STATUS
} from "src/modules/data-protection/constants";
import RiskAssessmentQuestionnaire from "./components/RiskAssessmentQuestionnaire";
import FriaQuestionnaire from "./components/FriaQuestionnaire";
import ConformityAssessmentQuestionnaire from "./components/ConformityAssessmentQuestionnaire";
import GetHelpFromAIIcon from "src/modules/data-protection/assets/images/microchip/black.png";
import AIRiskAssessmentModal from "src/modules/data-protection/components/RisksAndTasks/risks/ai/AssessmentModal";
import { RiRobotLine } from "react-icons/ri";
import AssessmentPanel from "../../components/AssessmentPanel";
import AssessmentSummaryOffcanvas from "../../components/summary-offcanvas";
import {
    mapEuAiActSummary,
    mapFriaSummary
} from "../../helpers/summaryCards";
import useComplianceModule, {
    COMPLIANCE_MODULE_SLUGS
} from "../../hooks/useComplianceModule";
import { getFriaSectionState } from "./helpers/getFriaSectionState";
import { fetchQuestionnaireQuestions } from "./helpers/fetchQuestionnaireQuestions";
import { AI_ACT_QUESTIONNAIRE_SLUGS } from "./helpers/getAiActQuestionnaireSlug";
import { isFeatureEnabledForActiveModule } from "src/helpers/module_helper";
import {
    readAssessmentQuestionFocusParams,
    focusAssessmentQuestionElement,
    hasAssessmentQuestionFocus
} from "../../helpers/assessmentQuestionFocus";

const CompleteWithAIConfirmationModal = ({
    t,
    isOpen,
    toggle,
    onConfirm,
    isLoading = false,
    title,
    description,
    helperText
}) => {
    return (
        <Modal
            isOpen={isOpen}
            toggle={toggle}
            centered
            size="md"
        >
            <ModalHeader toggle={toggle}>
                {title}
            </ModalHeader>

            <ModalBody>
                <div className="text-center">
                    <div
                        className="d-inline-flex align-items-center justify-content-center rounded-circle mb-4"
                        style={{
                            width: 72,
                            height: 72,
                            background: "rgba(13, 110, 253, 0.1)"
                        }}
                    >
                        <RiRobotLine
                            size={36}
                            className="text-primary"
                        />
                    </div>

                    <h5 className="mb-3">
                        {t("Generate AI Suggestions")}
                    </h5>

                    <p className="text-muted mb-0">
                        {description}
                    </p>

                    {helperText && (
                        <small className="text-muted d-block mt-3">
                            {helperText}
                        </small>
                    )}
                </div>
            </ModalBody>

            <ModalFooter>
                <Button
                    color="light"
                    onClick={toggle}
                    disabled={isLoading}
                >
                    {t("Cancel")}
                </Button>

                <Button
                    color="primary"
                    onClick={onConfirm}
                    disabled={isLoading}
                >
                    {isLoading
                        ? t("Generating...")
                        : t("Generate with AI")}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

const Questionnaires = ({
    t,
    token,
    onValidSubmit,
    refetchSoftwareData,
    refetchAssessmentCards,
    aiRiskAssessmentId,
    aiActQuestionnaireSlug,
    enableQuestionnaire,
    Organization,
    filledWithAIBefore,
    isQuestionnaireCompleted,
    friaOptions,
    conformityOptions,
    providerConformityRequired,
    activeAssessment,
    onProgressChange,
    complianceObligations = []
}) => {
    const { softwareId } = useParams();
    const history = useHistory();
    const location = useLocation();
    const focusParams = useMemo(() => {
        return readAssessmentQuestionFocusParams(location.search);
    }, [location.search]);

    const [isCreateAIOpen, setIsCreateAIOpen] = useState(false);
    const [isFriaCreateAIOpen, setIsFriaCreateAIOpen] = useState(false);
    const [friaQuestionnaireRemountKey, setFriaQuestionnaireRemountKey] =
        useState(0);
    const [aiBasedRiskAssessmentModalStatus, setAiBasedRiskAssessmentModalStatus] = useState(false);
    const [activeSummaryCard, setActiveSummaryCard] = useState(null);

    const {
        friaRequired,
        hasFriaAssessment,
        friaStatus,
        friaReportCaseId
    } = friaOptions;

    const {
        providerConformityStatus,
        conformityReportCaseId
    } = conformityOptions;

    const isCeConformityEnabled = isFeatureEnabledForActiveModule("ce-conformity");
    const isStandardAiActQuestionnaire =
        aiActQuestionnaireSlug === AI_ACT_QUESTIONNAIRE_SLUGS.standard;
    const showEuAiActAssessment = activeAssessment === "eu-ai-act";
    const showFriaAssessment = activeAssessment === "fria";
    const ceConformityAvailable =
        isCeConformityEnabled &&
        providerConformityRequired;
    const showCeConformityAssessment =
        ceConformityAvailable &&
        activeAssessment === "ce-conformity";

    const {
        data: euAiActComplianceModule,
        isError: euAiActSummaryIsError,
        isFetching: euAiActSummaryIsFetching,
        refetch: refetchEuAiActSummary
    } = useComplianceModule(
        softwareId,
        COMPLIANCE_MODULE_SLUGS.aiRiskAssessment,
        {
            enabled: showEuAiActAssessment,
            t
        }
    );
    const euAiActSummary = useMemo(() => {
        return mapEuAiActSummary(
            euAiActSummaryIsError
                ? null
                : euAiActComplianceModule?.summary
        );
    }, [euAiActComplianceModule, euAiActSummaryIsError]);

    const friaSectionState = getFriaSectionState({
        friaRequired,
        hasFriaAssessment,
        friaStatus
    });
    const {
        data: friaComplianceModule,
        isError: friaSummaryIsError,
        isFetching: friaSummaryIsFetching,
        refetch: refetchFriaSummary
    } = useComplianceModule(softwareId, COMPLIANCE_MODULE_SLUGS.fria, {
        enabled: showFriaAssessment,
        t
    });
    const friaSummary = useMemo(() => {
        return mapFriaSummary(
            friaSummaryIsError ? null : friaComplianceModule?.summary
        );
    }, [
        friaComplianceModule,
        friaSummaryIsError
    ]);

    const conformityStatusBadge = PROVIDER_CONFORMITY_STATUS[providerConformityStatus] || {
        color: "secondary",
        label: providerConformityStatus || "Unknown"
    };

    const sectionsIds = [
        aiActQuestionnaireSlug
    ];

    const questionsQueries = useQueries({
        queries: sectionsIds.map((sectionId) => {
            return {
                queryKey: ["data-protection-report-ai-assessment-questions-list", sectionId, softwareId],
                queryFn: () => fetchQuestionnaireQuestions(sectionId, softwareId),
                enabled: showEuAiActAssessment,
                refetchOnWindowFocus: false
            };
        })
    });

    const risksAssessmentQuery = questionsQueries[0];

    const friaQuestionsQuery = useQuery({
        queryKey: ["data-protection-report-ai-assessment-questions-list", "fria_assessment", softwareId],
        queryFn: () => fetchQuestionnaireQuestions("fria_assessment", softwareId),
        enabled: showFriaAssessment && friaSectionState.active,
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t("An error occurred while fetching questions."), {
                type: "error"
            });
        }
    });

    const reloadFriaAssessmentData = useCallback(async () => {
        await Promise.all([
            refetchSoftwareData?.(),
            refetchAssessmentCards?.(),
            refetchFriaSummary(),
            friaQuestionsQuery.refetch()
        ]);

        setFriaQuestionnaireRemountKey((key) => key + 1);
    }, [
        friaQuestionsQuery,
        refetchAssessmentCards,
        refetchFriaSummary,
        refetchSoftwareData
    ]);

    const conformityQuestionsQuery = useQuery({
        queryKey: ["data-protection-conformity-assessment-questions-list", softwareId],
        queryFn: () => fetchQuestionnaireQuestions("conformity_assessment", softwareId),
        enabled: showCeConformityAssessment,
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t("An error occurred while fetching questions."), {
                type: "error"
            });
        }
    });

    useEffect(() => {
        if (!showEuAiActAssessment || !hasAssessmentQuestionFocus(focusParams)) {
            return;
        }

        if (risksAssessmentQuery.isLoading || !risksAssessmentQuery.data) {
            return;
        }

        focusAssessmentQuestionElement(focusParams, {
            delayMs: 500,
            questions: risksAssessmentQuery.data?.mappedQuestions
        });
    }, [
        showEuAiActAssessment,
        focusParams,
        risksAssessmentQuery.isLoading,
        risksAssessmentQuery.data
    ]);

    useEffect(() => {
        if (!showFriaAssessment || !hasAssessmentQuestionFocus(focusParams)) {
            return;
        }

        if (!friaSectionState.active) {
            return;
        }

        if (friaQuestionsQuery.isFetching || !friaQuestionsQuery.data) {
            return;
        }

        focusAssessmentQuestionElement(focusParams, {
            delayMs: 500,
            questions: friaQuestionsQuery.data?.mappedQuestions
        });
    }, [
        showFriaAssessment,
        focusParams,
        friaSectionState.active,
        friaQuestionsQuery.isFetching,
        friaQuestionsQuery.data
    ]);

    useEffect(() => {
        if (activeAssessment !== "ce-conformity" || !hasAssessmentQuestionFocus(focusParams)) {
            return;
        }

        if (conformityQuestionsQuery.isFetching || !conformityQuestionsQuery.data) {
            return;
        }

        focusAssessmentQuestionElement(focusParams, {
            delayMs: 500,
            questions: conformityQuestionsQuery.data?.mappedQuestions
        });
    }, [
        activeAssessment,
        focusParams,
        conformityQuestionsQuery.isFetching,
        conformityQuestionsQuery.data
    ]);

    const completeWithAIMutation = useMutation({
        mutationFn: async () => {
            const response = await axios.post(
                `${API_BASE_URL}/report_case/fill_ai_act`,
                {
                    report_case_id: aiRiskAssessmentId
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            return response.data;
        },
        onSuccess: () => {
            risksAssessmentQuery.refetch();
            refetchEuAiActSummary();
        },
        onError: (error) => {
            if (process.env.NODE_ENV === "development") console.error(error);
            toast(t("An error occurred while generating by AI."), {
                type: "error"
            });
        },
        onSettled: () => {
            setIsCreateAIOpen(false);
        }
    });

    const completeFriaWithAIMutation = useMutation({
        mutationFn: async () => {
            const response = await axios.post(
                `${API_BASE_URL}/report_case/fill_fria`,
                {
                    report_case_id: friaReportCaseId
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            return response.data;
        },
        onSuccess: async () => {
            try {
                await reloadFriaAssessmentData();
            } catch (error) {
                if (process.env.NODE_ENV === "development") {
                    console.error(error);
                }
            }
        },
        onError: (error) => {
            if (process.env.NODE_ENV === "development") console.error(error);
            toast(t("An error occurred while generating by AI."), {
                type: "error"
            });
        },
        onSettled: () => {
            setIsFriaCreateAIOpen(false);
        }
    });

    return (
        <>
            <CompleteWithAIConfirmationModal
                isOpen={isCreateAIOpen}
                toggle={() => setIsCreateAIOpen(false)}
                onConfirm={completeWithAIMutation.mutate}
                isLoading={completeWithAIMutation.isLoading}
                t={t}
                title={t("Complete Risk Assessment with AI")}
                description={t("Would you like to complete the risk assessment using AI suggestions?")}
                helperText={t("AI will analyze the available information and generate responses for the risk assessment questionnaire. The generated responses will be applied automatically and can be reviewed or updated later.")}
            />

            <CompleteWithAIConfirmationModal
                isOpen={isFriaCreateAIOpen}
                toggle={() => setIsFriaCreateAIOpen(false)}
                onConfirm={completeFriaWithAIMutation.mutate}
                isLoading={completeFriaWithAIMutation.isLoading}
                t={t}
                title={t("Complete FRIA with AI")}
                description={t("Would you like to complete the FRIA assessment using AI suggestions?")}
                helperText={t("AI will analyze the available information and generate responses for the FRIA questionnaire. The generated responses will be saved automatically and can be reviewed and updated later.")}
            />

            {aiBasedRiskAssessmentModalStatus && (
                <AIRiskAssessmentModal
                    closeModal={() => setAiBasedRiskAssessmentModalStatus(false)}
                    onRisksCreated={() => {
                        history.push(`/admin/data-protection/softwares/${softwareId}?tab=risk-assessment`);
                    }}
                    isOpen={aiBasedRiskAssessmentModalStatus}
                    translateResult={true}
                    aiRequestOptions={{
                        requestUrl: `${CHAT_BOT_DOMAIN}api/gdpr/ai-risk-assessment/risks-associated-questioner.ai`,
                        payload: {
                            asset: softwareId,
                            organizationId: Organization.id,
                            questionnaireSlug: "ai_risk_assessment",
                            regenerate_risk_ai: undefined
                        }
                    }}
                    aiTranlateRequestOptions={{
                        requestUrl: `${CHAT_BOT_DOMAIN}api/gdpr/risks-associated-questioner-translate.ai`,
                        payload: {
                            assetId: softwareId,
                            module: "GDPR_AI_RISK_ASSESSMENT",
                            organizationId: Organization.id
                        }
                    }}
                    additionnalDetailsRequestOptions={{
                        requestUrl: API_URL_RISK_AI_GET_ADDITIONAL_DETAILS,
                        payload: {}
                    }}
                    createRisksRequestOptions={{
                        requestUrl: `${API_BASE_URL}/risk_rate/create_ai`,
                        payload: {
                            software: softwareId,
                            reportCase: aiRiskAssessmentId
                        }
                    }}
                >
                    {(result) => {
                        return (
                            <Modal
                                className="ai-risk-result-modal mw-100"
                                style={{ width: "90%" }}
                                isOpen={aiBasedRiskAssessmentModalStatus}
                                size="xl"
                            >
                                <ModalHeader toggle={() => setAiBasedRiskAssessmentModalStatus(!aiBasedRiskAssessmentModalStatus)}>
                                    {t("Risk Assesment Support by AI")}
                                </ModalHeader>

                                <ModalBody>
                                    <result.type {...result.props} />
                                </ModalBody>
                            </Modal>
                        );
                    }}
                </AIRiskAssessmentModal>
            )}

            {showEuAiActAssessment && (() => {
                const euAiActToolbar = (
                    <>
                        <Button
                            color="primary"
                            className="ai-base-risk-assessment-button"
                            onClick={() => setIsCreateAIOpen(true)}
                            disabled={risksAssessmentQuery.isFetching || filledWithAIBefore}
                        >
                            {t("Complete Risk Assessment with AI")}
                        </Button>

                        <div id="get-help-from-ai-button">
                            <Button
                                color="light"
                                className="ai-base-risk-assessment-button"
                                onClick={() => {
                                    setAiBasedRiskAssessmentModalStatus(true);
                                }}
                                outline
                                disabled={risksAssessmentQuery.isFetching || !isQuestionnaireCompleted}
                            >
                                <img alt="ai icon" src={GetHelpFromAIIcon} />
                                {t("Get help from AI")}
                            </Button>

                            {!isQuestionnaireCompleted && (
                                <UncontrolledTooltip
                                    target="get-help-from-ai-button"
                                >
                                    {t("Complete the questionnaire to generate AI-powered risks, recommendations, and tasks.")}
                                </UncontrolledTooltip>
                            )}
                        </div>
                    </>
                );

                const euAiActQuestionnaire = (
                    <RiskAssessmentQuestionnaire
                        questionsQuery={risksAssessmentQuery}
                        softwareId={softwareId}
                        aiRiskAssessmentId={aiRiskAssessmentId}
                        questionnaireSlug={aiActQuestionnaireSlug}
                        enableQuestionnaire={enableQuestionnaire}
                        onValidSubmit={(response) => {
                            refetchEuAiActSummary();
                            onValidSubmit && onValidSubmit(response);
                        }}
                        onProgressChange={onProgressChange}
                        setIsCreateAIOpen={setIsCreateAIOpen}
                        complianceObligations={complianceObligations}
                    />
                );

                return (
                    <AssessmentPanel
                        id="eu-ai-act-risk-assessment"
                        title={t("EU AI Act Assessment")}
                        titleBadge={
                            euAiActSummary.titleBadge
                                ? t(euAiActSummary.titleBadge)
                                : null
                        }
                        subtitle={t("Determine whether this AI system falls under the EU AI Act and classify its risk level.")}
                        toolbar={
                            isStandardAiActQuestionnaire
                                ? null
                                : euAiActToolbar
                        }
                        summaries={euAiActSummary.cards}
                        summariesLoading={
                            euAiActSummaryIsFetching &&
                            !euAiActComplianceModule
                        }
                        onSummaryAction={setActiveSummaryCard}
                    >
                        {euAiActQuestionnaire}
                    </AssessmentPanel>
                );
            })()}

            {showFriaAssessment && (() => {
                const friaAiButton = (
                    <Button
                        color="primary"
                        className="ai-base-risk-assessment-button"
                        outline
                        disabled={friaQuestionsQuery.isFetching}
                        onClick={() => setIsFriaCreateAIOpen(true)}
                    >
                        <img alt="ai icon" src={GetHelpFromAIIcon} />
                        {t("Complete FRIA with AI")}
                    </Button>
                );
                const disabledNotice = (
                    <div className="assessment-panel__disabled-notice">
                        <div className="d-flex align-items-start gap-2 text-muted">
                            <i
                                className="ri-information-line fs-5 flex-shrink-0"
                                id="fria-section-disabled-info"
                            />
                            <span>{t(friaSectionState.messageKey)}</span>
                            <UncontrolledTooltip target="fria-section-disabled-info">
                                {t(friaSectionState.tooltipKey)}
                            </UncontrolledTooltip>
                        </div>
                    </div>
                );
                const friaQuestionnaire = (
                    <FriaQuestionnaire
                        key={`fria-questionnaire-${friaQuestionnaireRemountKey}`}
                        questionsQuery={friaQuestionsQuery}
                        softwareId={softwareId}
                        friaReportCaseId={friaReportCaseId}
                        onValidSubmit={(response) => {
                            refetchFriaSummary();
                            onValidSubmit && onValidSubmit(response);
                        }}
                        onProgressChange={onProgressChange}
                    />
                );

                return (
                    <AssessmentPanel
                        id="fria-assessment"
                        title={t("FRIA Assessment")}
                        subtitle={t("Assess the impact of this AI system deployment on fundamental rights and track required FRIA activities.")}
                        toolbar={
                            friaSectionState.active
                                ? friaAiButton
                                : null
                        }
                        summaries={friaSummary.cards}
                        summariesLoading={
                            friaSummaryIsFetching &&
                            !friaComplianceModule
                        }
                    >
                        {friaSectionState.active
                            ? friaQuestionnaire
                            : <div className="p-3">{disabledNotice}</div>}
                    </AssessmentPanel>
                );
            })()}

            {showCeConformityAssessment && (() => {
                const conformityStatus = (
                    <Badge
                        color={conformityStatusBadge.color}
                        className={`bg-${conformityStatusBadge.color}`}
                        pill
                    >
                        {t(conformityStatusBadge.label)}
                    </Badge>
                );
                const conformityQuestionnaire = (
                    <ConformityAssessmentQuestionnaire
                        questionsQuery={conformityQuestionsQuery}
                        softwareId={softwareId}
                        conformityReportCaseId={conformityReportCaseId}
                        onValidSubmit={onValidSubmit}
                        onProgressChange={onProgressChange}
                        complianceObligations={complianceObligations}
                    />
                );

                return (
                    <AssessmentPanel
                        id="ce-conformity-assessment"
                        title={t("CE Conformity Assessment")}
                        subtitle={t("Document provider obligations required for CE marking and EU market placement.")}
                        toolbar={conformityStatus}
                    >
                        {conformityQuestionnaire}
                    </AssessmentPanel>
                );
            })()}

            <AssessmentSummaryOffcanvas
                isOpen={showEuAiActAssessment && !!activeSummaryCard}
                details={activeSummaryCard?.details}
                onClose={() => setActiveSummaryCard(null)}
            />
        </>
    );
};

const mapStatetoProps = (state) => {
    const { token, user } = state.Login;
    const { Organization } = state;

    return {
        token,
        user,
        Organization
    };
};

export default withNamespaces()(
    connect(mapStatetoProps)(
        Questionnaires
    )
);
